<template>
  <div>
    <div class="link-existing-tasks-panel__search">
      <el-input
        ref="searchRef"
        v-model="searchQuery"
        placeholder="Search tasks (e.g. #123, browser bug...)"
      />
    </div>
    <div class="link-existing-tasks-panel__results">
      <loading-placeholder v-if="isSearching" :lines="20" class="link-existing-tasks-panel__results__searching" />
      <template v-else>
        <div
          v-if="searchEmpty"
          class="link-existing-tasks-panel__results__hasnt-searched"
        >Your search results will display here.</div>
        <div v-else-if="showNoResults" class="link-existing-tasks-panel__results__no-results">
          <h5>No Search Results</h5>
          <p>Check if your task is already linked or search again.</p>
        </div>
        <div v-else class="link-existing-tasks-panel__results-list">
          <div v-for="(task, index) in matchingTasksToDisplay" :key="index">
            <div
              v-if="showProjectName(index)"
              class="link-existing-tasks-panel__results-list__row-entity-name"
            >{{task.project.name}}</div>
            <div
              v-if="!taskAlreadyLinked(task.id)"
              class="link-existing-tasks-panel__results-list__row-item"
            >
              <quick-add-button 
                class="link-existing-tasks-panel__results-list__row-item__add-button" 
                title="Add linked task"
                @click="linkSelectedTask(task.id)" 
              />
              <div
                :class="{'link-existing-tasks-panel__results-list__row-item-content-inactive' : !task.isActive }"
                class="link-existing-tasks-panel__results-list__row-item-content"
              >
                <span v-html="task.title" />
                <div v-if="showDescription(task)" v-html="task.description" />
              </div>
            </div>
          </div>
        </div>
      </template>
      <div class="link-existing-tasks-panel__results-footer">
        <dd-button type="flat" size="large" @click="$emit('canceled')">Close</dd-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { debounce } from 'lodash-es'

/* Mixins */

/* Components */

/* Helpers */

/* Data types */
import {
  IItemReference,
  ILinkedItemForListing,
  ISearchTaskResult,
} from '@/api/types/response.types'

/* API */
import SearchAPI from '@/api/modules/search.api'

export default defineComponent({
  name: 'LinkExistingTasksPanel',

  props: {
    defaultProjectId: {
      type: Number,
      default: null,
    },
    originatingTask: {
      type: Object as () => IItemReference,
      default: null,
    },
    currentlyLinkedTasks: {
      type: Array as () => ILinkedItemForListing[],
      required: true,
    },
  },

  emits: ['canceled', 'selected-existing-task-id', 'visible'],

  data() {
    return {
      isSearching: false,
      searchQuery: '',
      debounceGetResults: null as any,
      matchingTasks: [] as ISearchTaskResult[],
      searchedOnce: false,
      minSearchLength: 2,
    }
  },

  computed: {
    matchingTasksToDisplay(): ISearchTaskResult[] {
      const result = this.sortByProject(this.matchingTasks)

      if (this.originatingTask) {
        return result.filter(t => t.id !== this.originatingTask.id)
      }

      return result
    },
    searchEmpty(): boolean {
      return this.searchQuery.length < this.minSearchLength
    },
    noMatchingTasks(): boolean {
      return this.matchingTasksToDisplay.length === 0
    },
    showNoResults(): boolean {
      return (
        this.noMatchingTasks &&
        this.searchedOnce &&
        this.searchQuery.length >= this.minSearchLength
      )
    },
  },

  watch: {
    async searchQuery() {
      if (this.searchQuery.length >= 2) {
        await this.debounceGetResults()
        // @ts-ignore
        if (this.$refs.dropdownRef) {
          // @ts-ignore
          this.$refs.dropdownRef.$emit('visible', true)
        }
      } else {
        this.matchingTasks = [] as ISearchTaskResult[]
      }
    },
  },

  mounted() {
    // @ts-ignore
    this.$refs.searchRef.focus()
  },

  created() {
    this.debounceGetResults = debounce(this.getSearchTaskResults, 300)
  },

  methods: {
    // Show project name if the indexed item has a different project than its predecessor
    showProjectName(index: number): boolean {
      if (index === 0) {
        return true
      }

      if (
        this.matchingTasksToDisplay[index - 1].project.id !==
        this.matchingTasksToDisplay[index].project.id
      ) {
        return true
      }
    },

    sortByProject(tasks: ISearchTaskResult[]) {
      /* Show tasks in default project first */
      if (this.defaultProjectId) {
        const tasksInProject = [
          ...tasks.filter(t => t.project.id === this.defaultProjectId),
        ]
        const otherTasks = [
          ...tasks.filter(t => t.project.id !== this.defaultProjectId),
        ]

        tasksInProject.sort((a: ISearchTaskResult, b: ISearchTaskResult) => {
          if (a.isActive && !b.isActive) {
            return -1
          }
          if (!a.isActive && b.isActive) {
            return 1
          }
          return 0
        })

        otherTasks.sort((a: ISearchTaskResult, b: ISearchTaskResult) => {
          if (a.project.name.toLowerCase() < b.project.name.toLowerCase()) {
            return -1
          }
          if (a.project.name.toLowerCase() > b.project.name.toLowerCase()) {
            return 1
          }
          if (a.isActive && !b.isActive) {
            return -1
          }
          if (!a.isActive && b.isActive) {
            return 1
          }
          return 0
        })

        return [...tasksInProject, ...otherTasks]
      }

      tasks.sort((a: ISearchTaskResult, b: ISearchTaskResult) => {
        if (a.project.name.toLowerCase() < b.project.name.toLowerCase()) {
          return -1
        }
        if (a.project.name.toLowerCase() > b.project.name.toLowerCase()) {
          return 1
        }
        if (a.isActive && !b.isActive) {
          return -1
        }
        if (!a.isActive && b.isActive) {
          return 1
        }
        return 0
      })
      return tasks
    },

    // Only show description if there is a text match
    showDescription(task: ISearchTaskResult) {
      return task.description.includes('<span class="highlight">')
    },

    taskAlreadyLinked(id: number): boolean {
      return this.currentlyLinkedTasks.some(t => t.id === id)
    },

    async getSearchTaskResults() {
      if (this.searchQuery === '') {
        return
      }

      this.isSearching = true

      let result = await SearchAPI.post_Tasks(this.searchQuery)

      // Scrub away originating task, if matched...
      if (this.originatingTask) {
        result = result.filter(t => t.id !== this.originatingTask.id)
      }

      // Scrub away already linked tasks, if matched...
      result = result.filter(t => !this.taskAlreadyLinked(t.id))

      this.matchingTasks = result
      this.isSearching = false
      this.searchedOnce = true
    },

    linkSelectedTask(id: number) {
      this.$emit('selected-existing-task-id', id)
    },
  },
})
</script>

<style lang="scss">
@mixin result-row-position {
  position: relative;
  width: calc(100% + 60px);
  left: -30px;
}

.link-existing-tasks-panel {
  &__search {
    position: absolute;
    width: 100%;
    z-index: 2;
    padding: 20px 30px;
    background: var(--color-background-secondary);
    border-bottom: $border--solid-light;
    left: 0;
    top: 131px;
  }

  &__results {
    padding-top: 52px;

    &__searching {
      padding-top: 20px;
    }

    &__hasnt-searched {
      padding-top: 20px;
      font-size: 15px;
      color: var(--color-text-third);
    }

    &__no-results {
      padding-top: 20px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      > h5 {
        color: var(--color-text-third);
      }

      > p {
        color: var(--color-text-third);
      }
    }

    &-list {
      padding-bottom: 80px;

      &__row-entity-name {
        padding: 6px 30px;
        border-bottom: $border--solid-light;
        @include lettering--h5;
        @include result-row-position;
      }

      &__row-item {
        background-color: var(--color-background-header);
        display: flex;
        align-items: flex-start;
        padding: 10px 30px;
        border-bottom: solid 1px $light-blue-grey-two;
        @include result-row-position;

        &__add-button {
          margin-top: 3px;
        }

        &-content {
          padding-left: 10px;
          /* Ref Number & Title */
          > span {
            display: inline-block;
            @include lettering--p;
            font-weight: $bold;
          }
          /* Description */
          > div {
            > * {
              @include lettering--p-sm;
            }
          }

          &-inactive {
            opacity: 0.6;
          }
        }
      }
    }

    &-footer {
      display: flex;
      justify-content: flex-start;
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      padding: 20px 30px;
      background-color: var(--color-general-white);
      border-top: solid 1px $silver-two;
      /* Above result item checkboxes */
      z-index: 2;
    }
  }
}
</style>
